/**
 * "Ask Kio" — floating AI assistant on every dashboard route.
 *
 * Bottom-left FAB (the bottom-right slot is reserved for PanicFAB / SOS).
 * Clicking opens a right-side drawer with a message thread and an input.
 * Each `Send` POSTs the entire prior turn list to
 * `/v1/analytics/assistant/chat` and streams the server's reply token by
 * token into a new bubble.
 *
 * Stateless on the server — history lives only in this component's state,
 * cleared when the drawer is closed or the page is refreshed.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X, Send, Loader2, UserRound } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { cn } from '@/lib/utils';
import { getAccessToken, getCsrfToken } from '@/auth/tokenStore';
import { refreshAccessToken } from '@/auth/refresh';
import { usersApi } from '@/api/users';
import { useFocusedChild } from './KidFocusContext';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** True while tokens are still arriving on this message. */
  streaming?: boolean;
}

/** Open a streaming POST against /v1/analytics/assistant/chat and yield tokens. */
async function* streamAsk(
  messages: ChatMessage[],
  signal: AbortSignal,
  childId: string | null,
): AsyncGenerator<{ token?: string; error?: string; done?: boolean }> {
  const body = JSON.stringify({
    messages: messages.map((m) => ({ role: m.role, content: m.content })),
    ...(childId ? { child_id: childId } : {}),
  });
  const doFetch = (): Promise<Response> =>
    fetch(`${API_BASE}/v1/analytics/assistant/chat`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()!}` } : {}),
        'X-CSRF-Token': getCsrfToken() ?? '',
      },
      body,
    });

  let res = await doFetch();
  if (res.status === 401) {
    if (await refreshAccessToken()) res = await doFetch();
  }
  if (!res.ok || !res.body) {
    yield { error: `http_${res.status}`, done: true };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by blank lines. Each frame is a `data: …` line.
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 2);
      if (!frame.startsWith('data:')) continue;
      try {
        const evt = JSON.parse(frame.slice(5).trim());
        yield evt;
        if (evt.done) return;
      } catch {
        // ignore malformed frame
      }
    }
  }
}

export function AskKio() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Currently-focused child (set by ScreenTime / Timeline / Map pages).
  // We pass this to the server so "how is she doing?" resolves correctly,
  // and we show "Asking about <Name>" in the drawer header so the parent
  // sees what context Kio is using.
  const focusedChildId = useFocusedChild();
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
    staleTime: 60_000,
  });
  const focusedChild = focusedChildId
    ? children.data?.find((c) => c.id === focusedChildId) ?? null
    : null;

  // Auto-scroll to newest on stream tick.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setMessages((prev) =>
      prev.map((m, i) => (i === prev.length - 1 && m.streaming ? { ...m, streaming: false } : m)),
    );
  };

  const ask = async (text: string) => {
    const userMsg: ChatMessage = { role: 'user', content: text };
    const assistantMsg: ChatMessage = { role: 'assistant', content: '', streaming: true };
    const history = [...messages, userMsg];
    setMessages([...history, assistantMsg]);
    setDraft('');
    setBusy(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      for await (const evt of streamAsk(history, ctrl.signal, focusedChildId)) {
        if (evt.error) {
          setMessages((prev) => {
            const next = [...prev];
            const last = next.at(-1);
            if (last && last.role === 'assistant') {
              last.content = last.content || '(I couldn’t reach the assistant. Try again in a moment.)';
              last.streaming = false;
            }
            return next;
          });
          break;
        }
        if (evt.token) {
          setMessages((prev) => {
            const next = [...prev];
            const last = next.at(-1);
            if (last && last.role === 'assistant') {
              last.content += evt.token!;
            }
            return next;
          });
        }
        if (evt.done) break;
      }
    } catch {
      // AbortError or transport error — just mark the bubble as finished.
    } finally {
      setMessages((prev) =>
        prev.map((m, i) =>
          i === prev.length - 1 && m.streaming ? { ...m, streaming: false } : m,
        ),
      );
      abortRef.current = null;
      setBusy(false);
    }
  };

  const submitDraft = () => {
    const text = draft.trim();
    if (text && !busy) void ask(text);
  };

  return (
    <>
      {/* Floating launcher — bottom-right, sits ~80px above PanicFAB. Smaller
          than PanicFAB so the panic button stays the visual primary. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ask Kio"
        className="fixed bottom-24 right-5 z-30 grid size-12 place-items-center rounded-full bg-gradient-to-br from-[#4f6bff] via-[#7a5cff] to-[#ec4899] text-white shadow-lg shadow-[#7a5cff]/40 transition-transform hover:scale-[1.06]"
      >
        <Sparkles className="kz-sparkle size-5" />
      </button>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Ask Kio">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="border-b border-[color:var(--kz-color-border-subtle)] px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-[#4f6bff] via-[#7a5cff] to-[#ec4899] text-white">
                    <Sparkles className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold">Ask Kio</p>
                    <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">
                      AI assistant · sees only aggregate signals, never raw locations
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setOpen(false)}
                    className="grid size-9 place-items-center rounded-full text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]"
                  >
                    <X className="size-4" />
                  </button>
                </div>
                {focusedChild && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#4f6bff]/10 px-2.5 py-1 text-[11px] font-medium text-[#4f6bff]">
                    <UserRound className="size-3" />
                    Asking about {focusedChild.name}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
                {messages.length === 0 ? (
                  <KioEmptyState onPick={(q) => void ask(q)} />
                ) : (
                  <div className="flex flex-col gap-2">
                    {messages.map((m, i) => (
                      <Bubble key={i} message={m} />
                    ))}
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-[color:var(--kz-color-border-subtle)] px-4 py-3">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitDraft();
                  }}
                  className="flex items-end gap-2"
                >
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submitDraft();
                      }
                    }}
                    rows={1}
                    placeholder="Ask about your family this week…"
                    disabled={busy}
                    className="max-h-32 min-h-[40px] flex-1 resize-none rounded-2xl bg-[color:var(--kz-color-surface-sunken)] px-4 py-2 text-sm text-[color:var(--kz-color-ink-primary)] placeholder:text-[color:var(--kz-color-ink-muted)] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#4f6bff]/40"
                  />
                  {busy ? (
                    <button
                      type="button"
                      onClick={stop}
                      aria-label="Stop"
                      className="grid size-10 shrink-0 place-items-center rounded-full bg-rose-500 text-white hover:bg-rose-600"
                    >
                      <Loader2 className="size-4 animate-spin" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!draft.trim()}
                      aria-label="Send"
                      className={cn(
                        'grid size-10 shrink-0 place-items-center rounded-full transition-all',
                        draft.trim()
                          ? 'bg-[#4f6bff] text-white shadow-sm hover:bg-[#3a55ee]'
                          : 'cursor-not-allowed bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-muted)]',
                      )}
                    >
                      <Send className="size-4" />
                    </button>
                  )}
                </form>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const mine = message.role === 'user';
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug',
          mine
            ? 'rounded-br-md bg-[#4f6bff] text-white'
            : 'rounded-bl-md bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-primary)]',
        )}
      >
        <p className="whitespace-pre-wrap break-words">
          {message.content || (message.streaming ? '…' : '')}
        </p>
        {message.streaming && message.content && (
          <span className="ml-0.5 inline-block size-2 animate-pulse rounded-full bg-current opacity-50" />
        )}
      </div>
    </div>
  );
}

const SUGGESTIONS = [
  'How is my family doing this week?',
  'Any safety alerts I should know about?',
  'What was the busiest day this week?',
  'Tips to improve our family safety score?',
];

function KioEmptyState({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <span className="grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-[#4f6bff] via-[#7a5cff] to-[#ec4899] text-white shadow-lg shadow-[#7a5cff]/30">
        <Sparkles className="size-6" />
      </span>
      <div>
        <p className="text-lg font-bold">Hi, I&rsquo;m Kio.</p>
        <p className="mt-1 max-w-xs text-sm text-[color:var(--kz-color-ink-secondary)]">
          Ask me about your family&rsquo;s safety, recent activity, screen time, or
          anything you&rsquo;d normally hunt for in the app.
        </p>
      </div>
      <div className="grid w-full max-w-xs gap-2">
        {SUGGESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onPick(q)}
            className="rounded-xl border border-[color:var(--kz-color-border-subtle)] bg-white px-3 py-2 text-left text-xs text-[color:var(--kz-color-ink-secondary)] hover:border-[#4f6bff]/40 hover:bg-[#4f6bff]/5 hover:text-[color:var(--kz-color-ink-primary)]"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
